import { useCallback, useEffect, useRef, useState } from 'react'
import { queueAssistantChatOperation } from '@/lib/assistant-chat-operations'
import { useAssistantStore } from '@/stores/assistant-store'

type Owner = { current: boolean; generation: number; busy: boolean }
type Resolution = { threadId: string | undefined; isReady: boolean; error: string | null }

/** Shared org-chat lifecycle. A full-page thread URL takes precedence over the server's active thread. */
export function useAssistantChatLifecycle(threadId?: string) {
  const ownerRef = useRef<Owner | null>(null)
  const [resolution, setResolution] = useState<Resolution>({ threadId, isReady: !threadId, error: null })

  useEffect(() => {
    const owner: Owner = { current: true, generation: 0, busy: false }
    ownerRef.current = owner
    const resolveThread = (refresh = false) => {
      const generation = owner.generation
      const isCurrent = () => owner.current && owner.generation === generation
      return queueAssistantChatOperation(async () => {
        if (!isCurrent()) return
        let state = useAssistantStore.getState()
        // Home dispatches before navigating. Neither mount nor focus may replace
        // the optimistic messages or abort a matching live answer.
        if (state.sessionId && state.isStreaming && (!threadId || state.activeThreadId === threadId)) {
          if (threadId) setResolution({ threadId, isReady: true, error: null })
          return
        }
        owner.busy = true
        if (threadId) setResolution({ threadId, isReady: false, error: null })
        try {
          const missingFromCache = threadId && !state.threads.some(
            (thread) => thread.id === threadId && thread.sessionId === state.sessionId,
          )
          if (missingFromCache && state.isStreaming) {
            state.cancelStream()
            state = useAssistantStore.getState()
          }
          if (!state.sessionId || missingFromCache || (!state.isStreaming && (refresh || !threadId || state.activeThreadId === threadId))) {
            await state.reloadActiveThread(isCurrent)
            if (!isCurrent()) return
            state = useAssistantStore.getState()
            if (state.error) throw new Error(state.error)
          }
          if (threadId) {
            if (!state.threads.some((thread) => thread.id === threadId && thread.sessionId === state.sessionId)) {
              throw new Error('Conversation not found')
            }
            if (state.activeThreadId !== threadId) {
              await state.selectThread(threadId, isCurrent)
              if (!isCurrent()) return
              state = useAssistantStore.getState()
            }
            if (state.error || state.activeThreadId !== threadId) {
              throw new Error(state.error ?? 'Failed to open conversation')
            }
          }
          if (threadId) setResolution({ threadId, isReady: true, error: null })
        } catch (error) {
          if (threadId && isCurrent()) setResolution({ threadId, isReady: false,
            error: error instanceof Error ? error.message : 'Failed to open conversation' })
        } finally {
          owner.busy = false
        }
      })
    }
    void resolveThread()
    const handleFocus = () => { if (!owner.busy) void resolveThread(true) }
    window.addEventListener('focus', handleFocus)
    return () => {
      owner.current = false
      // The guarded abandoned action cannot settle its loading flag later.
      if (owner.busy) useAssistantStore.setState({ isLoading: false })
      window.removeEventListener('focus', handleFocus)
    }
  }, [threadId])

  const startNewChat = useCallback(async () => {
    const owner = ownerRef.current
    if (!owner?.current) return null
    const generation = ++owner.generation
    const isCurrent = () => owner.current && owner.generation === generation
    setResolution((state) => ({ ...state, isReady: false, error: null }))
    return queueAssistantChatOperation(async () => {
      if (!isCurrent()) return null
      owner.busy = true
      try {
        let state = useAssistantStore.getState()
        if (!state.sessionId) await state.reloadActiveThread(isCurrent)
        if (!isCurrent()) return null
        state = useAssistantStore.getState()
        if (!state.sessionId) throw new Error(state.error ?? 'Failed to start a new chat')
        await state.startNewChat(isCurrent)
        if (!isCurrent()) return null
        state = useAssistantStore.getState()
        if (state.error || !state.activeThreadId) throw new Error(state.error ?? 'Failed to start a new chat')
        setResolution({ threadId: state.activeThreadId, isReady: true, error: null })
        return state.activeThreadId
      } catch (error) {
        if (isCurrent()) setResolution((state) => ({ ...state, isReady: false,
          error: error instanceof Error ? error.message : 'Failed to start a new chat' }))
        return null
      } finally {
        owner.busy = false
      }
    })
  }, [])

  // Clean up orphaned streaming placeholders (older than 90 seconds).
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now()
      const state = useAssistantStore.getState()
      const orphanedPlaceholders = state.messages.filter((m) => m.isStreaming && now - new Date(m.createdAt).getTime() > 90_000)
      if (orphanedPlaceholders.length > 0) {
        useAssistantStore.setState({ messages: state.messages.filter((m) => !orphanedPlaceholders.includes(m)), isStreaming: false })
      }
    }, 30000)
    return () => clearInterval(cleanupInterval)
  }, [])

  return {
    isReady: !threadId || (resolution.threadId === threadId && resolution.isReady),
    error: threadId && resolution.threadId === threadId ? resolution.error : null,
    startNewChat,
  }
}
